import React, { useEffect, useRef, useState } from "react";

/**
 * CountUp — Anime un nombre de 0 (ou `from`) jusqu'à `to` quand entre dans le viewport.
 * Ne se déclenche qu'une seule fois. Respecte prefers-reduced-motion.
 *
 * Props :
 *   - to : valeur cible (nombre)
 *   - from : valeur de départ (défaut 0)
 *   - duration : ms (défaut 1200)
 *   - decimals : nb de décimales (défaut 0)
 *   - prefix / suffix : chaînes à concaténer
 *   - format : fn(value) → string (override)
 */
export default function CountUp({
  to,
  from = 0,
  duration = 1200,
  decimals = 0,
  prefix = "",
  suffix = "",
  format,
  className = "",
}) {
  const ref = useRef(null);
  const [value, setValue] = useState(from);
  const started = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setValue(to);
      started.current = true;
      return;
    }
    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();
          const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
          const tick = (now) => {
            const elapsed = now - startTime;
            const p = Math.min(elapsed / duration, 1);
            const eased = ease(p);
            setValue(from + (to - from) * eased);
            if (p < 1) requestAnimationFrame(tick);
            else setValue(to);
          };
          requestAnimationFrame(tick);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [to, from, duration]);

  const rendered = format
    ? format(value)
    : `${prefix}${Number(value).toLocaleString("fr-FR", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}${suffix}`;

  return (
    <span ref={ref} className={className}>
      {rendered}
    </span>
  );
}
